import { Crosshair, Minus, Plus } from "lucide-react";
import React from "react";
import { MAX_SCALE, MIN_SCALE } from "../gesture/useCanvasGestures";
import { useEditorStore } from "../store/EditorStoreContext";
import { useViewport } from "../store/selectors";

export interface ZoomPillProps {
  /** Zooms about the centre of this element. */
  canvasRef: React.RefObject<HTMLDivElement | null>;
}

const FIT_PADDING = 0.95;

const pillButton =
  "flex h-7 w-7 items-center justify-center rounded-md text-neutral-600 hover:bg-neutral-100 disabled:opacity-40";

/** Zoom out / current scale / zoom in / fit the editing area. */
export const ZoomPill: React.FC<ZoomPillProps> = ({ canvasRef }) => {
  const store = useEditorStore();
  const viewport = useViewport();

  const zoomTo = (scale: number) => {
    const bounds = canvasRef.current?.getBoundingClientRect();
    const centre = bounds
      ? { x: bounds.width / 2, y: bounds.height / 2 }
      : { x: 0, y: 0 };
    const clamped = Math.min(Math.max(scale, MIN_SCALE), MAX_SCALE);
    const worldX = (centre.x - viewport.x) / viewport.scale;
    const worldY = (centre.y - viewport.y) / viewport.scale;
    store.getState().setViewport({
      x: centre.x - worldX * clamped,
      y: centre.y - worldY * clamped,
      scale: clamped
    });
  };

  const fit = () => {
    const bounds = canvasRef.current?.getBoundingClientRect();
    if (!bounds) return;
    const { editingZoneWidth, editingZoneHeight } = store.getState().meta;
    const scale =
      Math.min(
        bounds.width / editingZoneWidth,
        bounds.height / editingZoneHeight
      ) * FIT_PADDING;
    store.getState().setViewport({
      x: bounds.width / 2 - (editingZoneWidth / 2) * scale,
      y: bounds.height / 2 - (editingZoneHeight / 2) * scale,
      scale
    });
  };

  return (
    <div className="absolute right-4 bottom-5 flex items-center gap-0.5 rounded-lg border border-neutral-200 bg-background-primary p-1 shadow-lg">
      <button
        type="button"
        className={pillButton}
        title="Zoom out"
        disabled={viewport.scale <= MIN_SCALE}
        onClick={() => zoomTo(viewport.scale / 1.2)}
      >
        <Minus size={16} />
      </button>
      <span className="min-w-11 flex-none text-center font-semibold text-[11.5px] text-neutral-600 [font-variant-numeric:tabular-nums]">
        {Math.round(viewport.scale * 100)}%
      </span>
      <button
        type="button"
        className={pillButton}
        title="Zoom in"
        disabled={viewport.scale >= MAX_SCALE}
        onClick={() => zoomTo(viewport.scale * 1.2)}
      >
        <Plus size={16} />
      </button>
      <div className="mx-0.5 h-4 w-px flex-none bg-neutral-200" />
      <button
        type="button"
        className={pillButton}
        title="Fit map"
        onClick={fit}
      >
        <Crosshair size={16} />
      </button>
    </div>
  );
};

export const useFitOnMount = (
  canvasRef: React.RefObject<HTMLDivElement | null>
): void => {
  const store = useEditorStore();
  React.useEffect(() => {
    // A host-provided camera (the map page's position) is kept as is.
    const current = store.getState().viewport;
    if (current.x !== 0 || current.y !== 0 || current.scale !== 1) return;
    const bounds = canvasRef.current?.getBoundingClientRect();
    if (!bounds || bounds.width === 0) return;
    const { editingZoneWidth, editingZoneHeight } = store.getState().meta;
    const scale =
      Math.min(
        bounds.width / editingZoneWidth,
        bounds.height / editingZoneHeight
      ) * FIT_PADDING;
    store.getState().setViewport({
      x: bounds.width / 2 - (editingZoneWidth / 2) * scale,
      y: bounds.height / 2 - (editingZoneHeight / 2) * scale,
      scale
    });
  }, [store, canvasRef]);
};
